import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "zone", "filename", "placeholder", "rejection"];

  declare readonly inputTarget: HTMLInputElement;
  declare readonly zoneTarget: HTMLElement;
  declare readonly filenameTarget: HTMLElement;
  declare readonly hasFilenameTarget: boolean;
  declare readonly placeholderTarget: HTMLElement;
  declare readonly hasPlaceholderTarget: boolean;
  declare readonly rejectionTarget: HTMLElement;
  declare readonly hasRejectionTarget: boolean;

  static classes = ["active"];
  declare readonly activeClasses: string[];

  private active = false;

  connect() {
    this.render();
  }

  open(event: Event) {
    if (event.target === this.inputTarget) return;
    event.preventDefault();
    this.inputTarget.click();
  }

  change() {
    this.setRejection(false);
    this.render();
  }

  dragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    this.activate();
  }

  dragLeave(event: DragEvent) {
    if (event.target === this.zoneTarget) this.deactivate();
  }

  drop(event: DragEvent) {
    event.preventDefault();
    this.deactivate();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const accepted = this.filterAccepted(files);
    if (accepted.length === 0) {
      this.setRejection(true);
      return;
    }

    const data = new DataTransfer();
    accepted.forEach((file) => data.items.add(file));
    this.inputTarget.files = data.files;
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  private filterAccepted(files: FileList): File[] {
    const accept = this.inputTarget.accept;
    if (!accept) return Array.from(files);

    const patterns = accept.split(",").map((p) => p.trim().toLowerCase());
    return Array.from(files).filter((file) => {
      const name = file.name.toLowerCase();
      const type = file.type.toLowerCase();
      return patterns.some((pattern) => {
        if (pattern.startsWith(".")) return name.endsWith(pattern);
        if (pattern.endsWith("/*"))
          return type.startsWith(pattern.slice(0, -1));
        return type === pattern;
      });
    });
  }

  private render() {
    const files = this.inputTarget.files;
    const hasFiles = !!files && files.length > 0;

    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = hasFiles
        ? Array.from(files!)
            .map((f) => f.name)
            .join(", ")
        : "";
      this.filenameTarget.hidden = !hasFiles;
    }

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.hidden = hasFiles;
    }

    if (hasFiles) {
      this.zoneTarget.dataset.hasFile = "";
    } else {
      delete this.zoneTarget.dataset.hasFile;
    }
  }

  private activate() {
    if (this.active) return;
    this.active = true;
    this.activeClasses.forEach((c) => this.zoneTarget.classList.add(c));
  }

  private deactivate() {
    if (!this.active) return;
    this.active = false;
    this.activeClasses.forEach((c) => this.zoneTarget.classList.remove(c));
  }

  private setRejection(visible: boolean) {
    if (this.hasRejectionTarget) this.rejectionTarget.hidden = !visible;
  }
}
